/**
 * icon-map — mapowanie string → Lucide React komponent ikony.
 *
 * Używane przez:
 *   - Sidebar (admin scope + tenant scope) — ikony z `config/menu.ts` ADMIN_MENU
 *     i z `MenuItem.iconName` w hierarchy.
 *   - MenuBuilderPage + komponenty menu — paletka, properties panel.
 *   - Renderowanie system items + custom modules + custom links.
 *
 * Wpisy MUSZĄ zachować spójność z `SYSTEM_ITEMS` w `packages/shared/src/menu.ts`
 * (iconName) oraz z `config/menu.ts` ADMIN_MENU (`icon` strings).
 */

import {
  Activity,
  AlertCircle,
  Archive,
  AtSign,
  Award,
  BadgeCheck,
  BadgeDollarSign,
  BarChart3,
  Bell,
  Book,
  Bookmark,
  Box,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Camera,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Coffee,
  Compass,
  Contact,
  Copy,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit,
  Euro,
  Eye,
  File,
  FileStack,
  FileText,
  Filter,
  Flag,
  Folder,
  Gift,
  GitBranch,
  Globe,
  Hash,
  Headphones,
  Heart,
  Home,
  Image as ImageIcon,
  Inbox,
  Info,
  Key,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  Library,
  Lightbulb,
  Link as LinkIcon,
  List,
  ListTree,
  type LucideIcon,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Mic,
  Minus,
  Monitor,
  Music,
  Package,
  Paperclip,
  Phone,
  PieChart,
  Plus,
  Receipt,
  Rocket,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Sun,
  Tag,
  Target,
  Ticket,
  Trash2,
  Truck,
  User,
  UserCog,
  Users,
  Video,
  Wallet,
  Wrench,
  Zap,
} from 'lucide-react';

export const ICON_MAP: Record<string, LucideIcon> = {
  // ADMIN_MENU
  LayoutDashboard,
  Package,
  BadgeDollarSign,
  Database,
  Building2,
  Receipt,
  FileText,
  Ticket,
  ShieldCheck,
  ScrollText,
  Boxes,
  LayoutTemplate,
  Library,
  FileStack,
  ListTree,
  GitBranch,
  // SYSTEM_ITEMS (tenant scope)
  UserCog,
  BadgeCheck,
  Briefcase,
  Contact,
  Settings,
  // Document classes (F20 T20.18)
  File,
  Mail,
  Shield,
  // Custom link / separator default
  Link: LinkIcon,
  Minus,
  Users,
  // Lowercase aliases (legacy compat z config/menu.ts)
  briefcase: Briefcase,
  users: Users,
  // IconPicker full set (sync — żeby resolveIcon nie fallobackował dla ikon wybranych w pickerze)
  Activity,
  AlertCircle,
  Archive,
  AtSign,
  Award,
  BarChart3,
  Bell,
  Book,
  Bookmark,
  Box,
  Building,
  Calendar,
  Camera,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Coffee,
  Compass,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  Edit,
  Euro,
  Eye,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  Hash,
  Headphones,
  Heart,
  Home,
  Image: ImageIcon,
  Inbox,
  Info,
  Key,
  Layers,
  Lightbulb,
  List,
  Map,
  MapPin,
  MessageSquare,
  Mic,
  Monitor,
  Music,
  Paperclip,
  Phone,
  PieChart,
  Plus,
  Rocket,
  Search,
  Send,
  Server,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Sun,
  Tag,
  Target,
  Trash2,
  Truck,
  User,
  Video,
  Wallet,
  Wrench,
  Zap,
};

export function resolveIcon(
  name: string | null | undefined,
  fallback: LucideIcon = Package,
): LucideIcon {
  if (!name) return fallback;
  return ICON_MAP[name] ?? fallback;
}
